import { ChevronRight, Lock } from "lucide-react";

import { AccessCardField } from "@/modules/dashboard/settings/components/access/AccessCardField";
import { AccessScopeBadge } from "@/modules/dashboard/settings/components/access/AccessScopeBadge";
import { roleColor } from "@/modules/dashboard/settings/utils/roleColor";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { RoleRow } from "../hooks/useRolesCatalog";
import { useRoleReach } from "../utils/roleReach";
import { RolePoliciesCell } from "./RolePoliciesCell";

export interface RolesCardsProps {
  rows: readonly RoleRow[];
  loading: boolean;
  isInternalUser: boolean;
  onOpen: (row: RoleRow) => void;
}

/** The same rows as `RolesTable`, below `lg`. Same data, same opening behaviour, one card per role. */
export function RolesCards({ rows, loading, isInternalUser, onOpen }: RolesCardsProps) {
  const { t } = useTranslations("settings");
  const reachOf = useRoleReach();

  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <ColocaSkeleton className="h-32 w-full" />
        <ColocaSkeleton className="h-32 w-full" />
        <ColocaSkeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {rows.map((row) => {
        const { role } = row;
        const reach = reachOf(role);
        return (
          <button
            key={role.id}
            type="button"
            onClick={() => onOpen(row)}
            className={cn(
              "relative flex flex-col gap-2.5 rounded-md border border-border bg-card p-3.5 text-left transition-colors hover:border-border/70",
              !row.editable && "opacity-70",
            )}
          >
            <span
              className="absolute bottom-0 left-0 top-0 w-[3px] rounded-l-md"
              style={{ background: roleColor(role.slug) }}
              aria-hidden
            />
            <div className="flex items-center gap-1.5 pl-1.5">
              <span className="truncate text-[13.5px] font-semibold text-foreground">{role.name}</span>
              {role.is_protected && (
                <Lock
                  size={11}
                  className="shrink-0 text-muted-foreground/70"
                  aria-label={t("access_roles_protected_hint")}
                />
              )}
              {role.is_default && (
                <ColocaBadge variant="secondary">{t("access_role_default_badge")}</ColocaBadge>
              )}
              <ChevronRight size={14} className="ml-auto shrink-0 text-muted-foreground/60" />
            </div>

            <div className="pl-1.5 font-mono text-[10.5px] text-muted-foreground">{role.slug}</div>

            <div className="grid grid-cols-2 gap-3 pl-1.5">
              <AccessCardField label={t("access_page_col_scope")}>
                <AccessScopeBadge organizationId={role.organization_id} />
              </AccessCardField>
              <AccessCardField label={t("access_roles_col_reach")}>
                <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <reach.icon size={12} className="shrink-0" />
                  <span className="truncate">{reach.label}</span>
                </span>
              </AccessCardField>
            </div>

            <div className="pl-1.5">
              <AccessCardField label={t("access_roles_col_policies")}>
                <RolePoliciesCell policies={role.policies} />
              </AccessCardField>
            </div>

            <div className="border-t border-dashed border-border pl-1.5 pt-2 font-mono text-[10.5px] text-muted-foreground">
              {isInternalUser
                ? role.member_count === 1
                  ? t("access_role_member_count_platform_one")
                  : t("access_role_member_count_platform", { count: role.member_count })
                : role.member_count === 1
                  ? t("access_role_member_count_org_one")
                  : t("access_role_member_count_org", { count: role.member_count })}
            </div>
          </button>
        );
      })}
    </div>
  );
}
